import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as EmptyGraphic } from '@elementary/common-ui/assets/error-graphic.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/slack-color-small.svg';
import { Button, FlexColumn } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { useErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useViewErrorsTracking from '@elementary/common-ui/hooks/tracking/useViewErrorsTracking';

const ButtonStyled = styled(Button)`
  margin-top: 8px;
`;

const Link = styled.a`
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const EmptyGraphicStyled = styled(EmptyGraphic)`
  margin-bottom: 50px;
`;

const ErrorPage = ({ error, page }: { error: Error; page?: string }) => {
  const [loaded, setLoaded] = useState(false);
  const { resetBoundary } = useErrorBoundary();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useViewErrorsTracking({
    error,
    context: {
      component: 'ReportErrorPage',
      page,
      previousRoute: document.referrer,
      appState: {
        searchParams: Object.fromEntries(searchParams.entries()),
        currentRoute: pathname,
      },
      tags: ['error-boundary', 'report-app'],
    },
  });

  useEffect(() => {
    if (loaded) {
      resetBoundary();
      setSearchParams();
    }
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <FlexColumn
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <EmptyGraphicStyled />
      <Typography sx={{ fontSize: '34px', fontWeight: 'bold' }}>
        Oops..
      </Typography>
      <Typography
        sx={{
          whiteSpace: 'pre-wrap',
          textAlign: 'center',
          color: 'common.gray2',
          fontSize: '16px',
        }}
      >
        Sorry, something went wrong. Please see our{' '}
        <Link
          href="https://docs.elementary-data.com/general/troubleshooting"
          target="_blank"
        >
          troubleshooting doc
        </Link>
        <br />
        or reach out to the Elementary team on Slack - we are always happy to
        help!
      </Typography>
      <ButtonStyled
        variant="outlined"
        color="secondary"
        endIcon={<SlackIcon />}
        onClick={() =>
          window.open(
            'https://join.slack.com/t/elementary-community/shared_invite/zt-uehfrq2f-zXeVTtXrjYRbdE_V6xq4Rg',
          )
        }
      >
        Support on Slack
      </ButtonStyled>
    </FlexColumn>
  );
};

export default ErrorPage;
